import './styles.css';
import { html, raw, render, $, toast, modal, icon } from './ui.js';
import { t, setLang, detectLang, getLang } from './i18n.js';
import { loadQueue, onQueueChange, flush } from './offline.js';

const root = $('#app');
const ctx = { me: null, rerenderShell: () => shell() };
let auth = null;
let cleanup = null;
let routeSeq = 0;

// ------------------------------------------------------------------ routes
const routes = [
  [/^#?\/?$/, () => import('./views/home.js').then((m) => m.homeView)],
  [/^#\/calendar$/, () => import('./views/calendar.js').then((m) => m.calendarView)],
  [/^#\/classes$/, () => import('./views/classes.js').then((m) => m.classesView)],
  [/^#\/archived$/, () => import('./views/classes.js').then((m) => m.archivedView)],
  [/^#\/insights(?:\/([^/]+))?$/, () => import('./views/insights.js').then((m) => m.insightsView), (m) => ({ id: m[1] })],
  [/^#\/report\/([^/]+)$/, () => import('./views/reports.js').then((m) => m.reportView), (m) => ({ id: m[1] })],
  [/^#\/more$/, () => import('./views/settings.js').then((m) => m.moreView)],
  [/^#\/class\/([^/]+)(?:\/(\w+))?$/, () => import('./views/classview.js').then((m) => m.classView), (m) => ({ id: m[1], tab: m[2] })],
  [/^#\/live\/([^/]+)$/, () => import('./views/live.js').then((m) => m.liveView), (m) => ({ id: m[1] })],
  [/^#\/kiosk\/([^/]+)$/, () => import('./views/kiosk.js').then((m) => m.kioskView), (m) => ({ id: m[1] })],
  [/^#\/student\/([^/]+)$/, () => import('./views/student.js').then((m) => m.studentView), (m) => ({ id: m[1] })],
  [/^#\/done\/([^/]+)$/, () => import('./views/done.js').then((m) => m.doneView), (m) => ({ id: m[1] })],
];

// ---------------------------------------------------------------- navigation
const NAV = [['home', '#/', 'home', 'nav.home'], ['calendar', '#/calendar', 'calendar_month', 'nav.calendar'], ['classes', '#/classes', 'school', 'nav.classes'], ['insights', '#/insights', 'monitoring', 'nav.insights'], ['more', '#/more', 'menu', 'nav.more']];
function activeTab(h) {
  if (/^#?\/?$/.test(h) || /^#\/done\//.test(h)) return 'home';
  if (/^#\/calendar/.test(h)) return 'calendar';
  if (/^#\/(classes|class|archived|student)/.test(h)) return 'classes';
  if (/^#\/(insights|report)/.test(h)) return 'insights';
  if (/^#\/more/.test(h)) return 'more';
  return '';
}
function paintNav() {
  const h = location.hash || '#/';
  const a = activeTab(h);
  document.body.classList.toggle('no-tabs', /^#\/(live|kiosk|done)\//.test(h));
  document.querySelectorAll('[data-tab]').forEach((el) => el.classList.toggle('on', el.dataset.tab === a));
}

async function route() {
  const seq = ++routeSeq;
  paintNav();
  const tt = $('#toast'); if (tt) tt.hidden = true;
  if (location.hash === '#/settings') { location.hash = '#/more'; return; }
  if (/^#\/reports/.test(location.hash)) { location.hash = '#/insights'; return; }
  if (typeof cleanup === 'function') { try { cleanup(); } catch { /* ignore */ } }
  cleanup = null;
  if (!ctx.me) return;
  const hash = location.hash || '#/';
  for (const [re, load, params] of routes) {
    const m = hash.match(re);
    if (!m) continue;
    try {
      const view = await load();
      if (seq !== routeSeq) return;
      const c = await view(root, params ? params(m) : {}, ctx);
      if (seq !== routeSeq) { if (typeof c === 'function') c(); return; }
      cleanup = c;
    } catch (e) {
      if (seq !== routeSeq) return;
      console.error(e);
      render(root, html`<div class="page"><div class="empty"><p>${e.status === 404 ? t('error.notfound') : t('error.generic')}</p>
        <a class="btn" href="#/">${t('common.back')}</a></div></div>`);
    }
    window.scrollTo(0, 0);
    return;
  }
  location.hash = '#/';
}

// ------------------------------------------------------------------- shell
function shell() {
  const foot = $('#foot'), bar = $('#tabbar');
  if (ctx.me) {
    render(bar, html`${NAV.map(([k, href, ic, lbl]) => raw(html`<a href="${href}" data-tab="${k}"><span class="pill-i">${icon(ic)}</span><span>${t(lbl)}</span></a>`))}`);
    bar.hidden = false; foot.hidden = true;
    paintNav();
    paintNet();
  } else {
    bar.hidden = true; foot.hidden = false;
    render(foot, html`<span>${t('footer.by')} <a href="https://codingbubble.com" target="_blank" rel="noopener">CodingBubble</a> · ${t('footer.tagline')}</span><a href="/privacy">${t('footer.privacy')}</a><a href="/terms">${t('footer.terms')}</a>`);
  }
}

ctx.feedback = () => feedback();
async function feedback() {
  const msg = await modal(html`<form id="f" class="form"><h2>${t('footer.feedback')}</h2><textarea name="m" rows="5" required maxlength="2000" placeholder="${t('feedback.ph')}"></textarea>
    <div class="row end"><button type="button" class="btn" data-x="c">${t('common.cancel')}</button><button class="btn primary">${t('feedback.send')}</button></div></form>`,
  (d, close) => { $('[data-x=c]', d).onclick = () => close(); $('#f', d).onsubmit = (e) => { e.preventDefault(); close($('textarea', d).value); }; });
  if (!msg?.trim()) return;
  const { post } = await import('./api.js');
  try { await post('/api/feedback', { message: msg, page: location.hash }); toast(t('feedback.thanks')); } catch (e) { toast(e.message); }
}

let pendingN = 0;
function paintNet() {
  const el = $('#netpill');
  if (!el) return;
  const off = !navigator.onLine;
  el.hidden = !ctx.me || (!off && !pendingN);
  el.className = 'netpill' + (off ? ' off' : '');
  el.textContent = off ? t('net.offline') + (pendingN ? ` · ${pendingN}` : '') : t('net.pending', { n: pendingN });
}
onQueueChange(({ pending }) => { pendingN = pending; paintNet(); });
window.addEventListener('online', () => { paintNet(); flush(); });
window.addEventListener('offline', paintNet);

// -------------------------------------------------------------------- boot
async function onUser(user) {
  if (!user) {
    ctx.me = null;
    setLang(detectLang());
    shell();
    (await import('./views/login.js')).loginView(root);
    return;
  }
  try {
    const { get } = await import('./api.js');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    ctx.me = await get(`/api/me?tz=${encodeURIComponent(tz)}`);
    setLang(detectLang(ctx.me.language));
    await loadQueue();
    flush();
    shell();
    route();
  } catch (e) {
    ctx.me = null;
    shell();
    const denied = e.status === 403;
    render(root, html`<div class="page narrow"><div class="empty"><p>${denied ? t('error.not_allowed') : t('error.generic')}</p>
      <button class="btn" id="so">${t('nav.signout')}</button></div></div>`);
    $('#so').onclick = () => auth.signOut();
  }
}

async function boot() {
  setLang(detectLang());
  try {
    auth = (await import('./auth.js')).default;
  } catch (e) {
    console.error(e);
    render(root, html`<div class="page narrow"><div class="empty"><p>Sign-in is not configured yet.</p></div></div>`);
    return;
  }
  window.addEventListener('hashchange', route);
  auth.onChange(onUser);
  if ('serviceWorker' in navigator && location.hostname !== 'localhost') navigator.serviceWorker.register('/sw.js').catch(() => {});
}
boot();
